import { AESEncryptionKey, AESKeySize } from "@-/com.npmjs.expo-crypto";
import pMemoize from "@-/com.npmjs.p-memoize";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { backupStore } from "#backup-store";

/**
 * Getting the key waits for the cross-tab key lock and then does at most two backup-store reads,
 * one key generation and one write — each store operation already bounded. 60s covers another tab
 * holding the lock through the same work. Bounded because the result is memoized: a lock request
 * that never resolved used to pin every backup encryption in the page forever. A timed-out attempt
 * withdraws its lock request and is not cached (`pMemoize` drops rejections), so the next backup
 * tries again.
 */
const BACKUP_KEY_DEADLINE_MS = 60_000;

// Browser keys share origin storage. This is integrity protection, not a claim
// of secrecy from code running on the same origin.
export const getBackupKey = pMemoize(async () =>
  withDeadline(
    { deadline: BACKUP_KEY_DEADLINE_MS, operation: "load the backup key" },
    async (signal) =>
      navigator.locks.request("haniverse-backup-key", { signal }, async () => {
        const stored = await backupStore.get("encryption-key");
        if (stored !== null) {
          return AESEncryptionKey.import(stored, "base64");
        }
        const archives = await backupStore.keys("archive/");
        if (archives.length > 0) {
          throw new Error("The key for existing local backups is unavailable");
        }
        const key = await AESEncryptionKey.generate(AESKeySize.AES256);
        await backupStore.set("encryption-key", await key.encoded("base64"));
        return key;
      }),
  ),
);
